import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { FileText } from 'lucide-react'

import { PageContainer } from '@/components/common/page-container'
import { PageHeader } from '@/components/common/page-header'
import { EmptyState } from '@/components/ui/empty-state'
import { CVUpload } from '@/features/cv/cv-upload'
import { CVList } from '@/features/cv/cv-list'
import { CVPreview } from '@/features/cv/cv-preview'
import { CVAnalysisPanel } from '@/features/ai/cv-analysis-panel'
import { cvsListQuery, type CV } from '@/api/cvs'
import { loopsListQuery } from '@/api/loops'
import { RouteError } from '@/components/common/route-error'

/**
 * /cv — list + upload (mockup 04, AI side wired in M4).
 *
 * Selection state is local; the selected CV id seeds AI screens in M4
 * (CV analysis on the right column). For now we just show the parsed
 * resumeText preview.
 *
 * Loops list is loaded here (best-effort, non-suspending) so the
 * CV list can render "Used by N loops" badges and translate a
 * delete-409 loopId back into a loop name in the error toast.
 */
export const Route = createFileRoute('/_authed/cv')({
  loader: ({ context }) => context.queryClient.ensureQueryData(cvsListQuery(1, 100)),
  component: CVPage,
  errorComponent: RouteError,
})

function CVPage() {
  const { data } = useSuspenseQuery(cvsListQuery(1, 100))
  // Loops are decorative on this page — render the rest of the screen
  // even if the loops query is slow/erroring.
  const loopsQ = useQuery(loopsListQuery(1, 100))
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined)

  // Auto-select the first (newest) CV when list changes and nothing is selected.
  useEffect(() => {
    if (!selectedId && data.items.length > 0) {
      const first = data.items[0]
      if (first) setSelectedId(first.id)
    }
  }, [data.items, selectedId])

  const selectedCV = data.items.find((cv) => cv.id === selectedId)

  return (
    <PageContainer>
      <PageHeader
        title="My CV"
        description="Upload your resume. We send the parsed text to LoopCV so it can match jobs, and to our LLM for scoring (M4)."
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
        <div className="flex flex-col gap-4">
          <CVUpload onUploaded={(id) => setSelectedId(id)} />
          {data.items.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="No CVs yet"
              description="Upload a DOCX or PDF to get started."
            />
          ) : (
            <CVList
              items={data.items}
              selectedId={selectedId}
              onSelect={(cv: CV) => setSelectedId(cv.id)}
              loops={loopsQ.data?.items}
            />
          )}
        </div>

        <div className="flex flex-col gap-4">
          {selectedCV ? (
            <>
              <CVAnalysisPanel cvId={selectedCV.id} />
              <CVPreview cvId={selectedCV.id} />
            </>
          ) : (
            <EmptyState
              icon={FileText}
              title="Select a CV"
              description="Pick one from the list to preview the parsed text and run AI analysis."
            />
          )}
        </div>
      </div>
    </PageContainer>
  )
}
